import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "@/components/sidebar/Sidebar";
import { MobileSidebar } from "@/components/sidebar/MobileSidebar";
import { Header } from "@/components/header/Header";
import { AIPanel } from "@/features/ai/panel/AIPanel";
import { CommandPalette } from "@/components/CommandPalette";
import { useAllBusinessUnits } from "@/hooks/useAllBusinessUnits";
import { useSyncUserProfile } from "@/hooks/useSyncUserProfile";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import { SubscriptionStatusGuard } from "@/components/shared/SubscriptionStatusGuard";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { CompanyTimezoneBoundary } from "@/components/shared/CompanyTimezoneBoundary";

function WorkspaceData() {
  useAllBusinessUnits();
  return null;
}

export function DashboardLayout() {
  useSyncUserProfile();
  const { allowed } = useSubscriptionAccess();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const aiAssistantEnabled = useAiAssistantEnabled();
  const handleCloseMobileSidebar = useCallback(
    () => setMobileSidebarOpen(false),
    [],
  );
  const handleOpenMobileSidebar = useCallback(
    () => setMobileSidebarOpen(true),
    [],
  );

  // Measure the combined page/panel region. Its width responds to the viewport
  // and sidebar, but not to the AI panel opening or switching to an overlay.
  // Measuring the page alone creates a dock/overlay resize feedback loop.
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width == null) return;
      setContentWidth((prev) => (Math.abs(prev - width) > 1 ? width : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex h-dvh lg:h-svh overflow-hidden bg-background">
      {allowed && <WorkspaceData />}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-xl focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to content
      </a>

      <Sidebar />
      <MobileSidebar
        open={mobileSidebarOpen}
        onClose={handleCloseMobileSidebar}
      />

      <div ref={contentRef} className="flex min-w-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Header
            onOpenMobileSidebar={handleOpenMobileSidebar}
            workspaceReady={allowed}
          />
          <main
            id="main-content"
            className="flex-1 overflow-auto bg-muted/20 dark:bg-muted/10"
          >
            {/* Subtle mesh gradient background */}
            <div className="relative h-full">
              <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-40">
                <div className="absolute top-0 right-0 h-[600px] w-[600px] bg-primary/[0.02] rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 h-[400px] w-[400px] bg-accent/[0.02] rounded-full blur-[100px] translate-y-1/2 -translate-x-1/3" />
              </div>
              <div className="relative h-full">
                <SubscriptionStatusGuard>
                  <CompanyTimezoneBoundary>
                    <Outlet />
                  </CompanyTimezoneBoundary>
                </SubscriptionStatusGuard>
              </div>
            </div>
          </main>
        </div>

        {allowed && aiAssistantEnabled && (
          <AIPanel contentWidth={contentWidth} />
        )}
      </div>
      {allowed && <CommandPalette />}
    </div>
  );
}
